import { useSelector, useDispatch } from 'react-redux';
import { DragDropContext, Draggable, Droppable } from "react-beautiful-dnd";
import { zoomOnWaypoint, addWaypoint as addInqueryWaypoint, removeWaypoint as removeInquieryWaypoint, setWaypointDepart, swapWaypoints, removeWaypoint, updateRouteEstimation } from '../../store/inquerySlice';
import RouteAddress from './RouteAddress';
import RouteWaypointSearch from './RouteWaypointSearch';
import { Waveform } from '@uiball/loaders';
import { unwrapResult } from '@reduxjs/toolkit';
import { useEffect, useRef, useCallback } from 'react';

let WAYPOINT_ID_COUNTER = 0;

const TIMEZONE_OFFSET_MINUTES = new Date().getTimezoneOffset();
const TIMEZONE_OFFSET_SECONDS = TIMEZONE_OFFSET_MINUTES * 60;

// Today + 1 week, in UNIX TIME
const DEFAULT_DEPART = Math.floor((Date.now() + 7 * 24 * 60 * 60 * 1000) / 1000);

// Helper to convert UNIX timestamp (seconds) to 'YYYY-MM-DDTHH:mm' for datetime-local input
function unixToDateTimeLocalString(timestamp) {
    if (!timestamp) return '';
    const date = new Date(timestamp * 1000);
    // Adjust for local timezone offset before formatting
    const adjustedDate = new Date(date.getTime() - (date.getTimezoneOffset() * 60000));
    return adjustedDate.toISOString().slice(0, 16);
}

// Helper to convert 'YYYY-MM-DDTHH:mm' from datetime-local input to UNIX timestamp (seconds)
function dateTimeLocalStringToUnix(dateTimeString) {
    if (!dateTimeString) return null;
    const date = new Date(dateTimeString);
    // No timezone adjustment needed here as Date.parse interprets it as local time
    return Math.floor(date.getTime() / 1000);
}

function unixToEstonianDateTimeString(timestamp) {
    // Create a Date object from the timestamp (in milliseconds)
    const date = new Date(timestamp * 1000);
    
    // Format in human readable form, display month
    // Use JS' built in formatter
    return date.toLocaleString('et-EE', { year: 'numeric', month: 'long', day: 'numeric', hour: 'numeric', minute: 'numeric' });
}

const RouteDisplay = (props) => {
    const dispatch = useDispatch();

    const waypoints = useSelector((state) => state.inquery.waypoints);

    // --- Throttling Logic ---
    const timerRef = useRef(null);
    const isWaitingRef = useRef(false);

    const scheduleUpdateEstimation = useCallback(() => {
        // If already waiting for timeout, ignore subsequent calls
        if (isWaitingRef.current) {
            console.log("Throttled: Update already scheduled.");
            return;
        }

        // Set flag and start timer
        isWaitingRef.current = true;
        console.log("Scheduling route estimation update in 3s.");

        // Clear any potentially lingering timer (safety measure)
        if (timerRef.current) {
            clearTimeout(timerRef.current);
        }

        timerRef.current = setTimeout(() => {
            console.log("Executing throttled route estimation update.");
            dispatch(updateRouteEstimation()); // Thunk uses getState for latest data
            isWaitingRef.current = false; // Allow new triggers
            timerRef.current = null;
        }, 3000); // 3-second delay

    }, [dispatch]); // Dependency: dispatch function

    // Cleanup timer on component unmount
    useEffect(() => {
        return () => {
            if (timerRef.current) {
                clearTimeout(timerRef.current);
                console.log("Cleared route estimation timer on unmount.");
            }
        };
    }, []);
    // --- End Throttling Logic ---

    // --- Initial Load Estimation ---
    useEffect(() => {
        // Check if there's a valid route loaded initially (more than 1 point, first has location & depart)
        if (waypoints.length > 1 && waypoints[0].location && waypoints[0].depart) {
            console.log("Initial route detected, scheduling estimation.");
            
            // Don't schedule, just run it immediately
            dispatch(updateRouteEstimation());

            // Cancel any existing timer
            if (timerRef.current) {
                clearTimeout(timerRef.current);
                timerRef.current = null;
            }
            isWaitingRef.current = false;
        }
        // Run only once on mount
    }, [scheduleUpdateEstimation]); // Include scheduleUpdateEstimation in dependency array
    // --- End Initial Load Estimation ---

    function addWaypoint(ref, place) {
        // Modified: Removed place_id
        const waypoint = {
            formatted_address: place.formatted_address,
            location: {
                lat: place.geometry.location.lat(),
                lng: place.geometry.location.lng(),
            },
            depart: null, // Initialize depart time
        };

        dispatch(addInqueryWaypoint({ waypoint }));
        scheduleUpdateEstimation(); // Use throttled function
        dispatch(zoomOnWaypoint({
            lat: place.geometry.location.lat(),
            lng: place.geometry.location.lng(),
        }));
    }

    function handleOnDragEnd(result) {
        if (!result.destination) return;

        dispatch(swapWaypoints({ fromIndex: result.source.index, toIndex: result.destination.index }));
        scheduleUpdateEstimation(); // Use throttled function
    }

    function handleOnDelete(id) {
        dispatch(removeWaypoint({ id: ""+id }));
        scheduleUpdateEstimation(); // Use throttled function
    }

    // Modified: Convert input value to UNIX timestamp before dispatching
    async function handleChangeDepart(id, datetimeLocalString) {
        const departTimestamp = dateTimeLocalStringToUnix(datetimeLocalString);
        dispatch(setWaypointDepart({
            id,
            depart: departTimestamp
        }));
        scheduleUpdateEstimation(); // Use throttled function
    }

    function sameDepartAsArrive(id) {
        const waypoint = waypoints.find(w => {
            return +w.id === +id;
        });

        // Check if arrive time exists before setting
        if (waypoint && waypoint.arrive !== null) {
            dispatch(setWaypointDepart({
                id,
                depart: waypoint.arrive
            }));
            dispatch(updateRouteEstimation()); // Update estimation immediately
            // Cancel ongoing timer
            if (timerRef.current) {
                clearTimeout(timerRef.current);
                timerRef.current = null;
            }
            isWaitingRef.current = false; // Reset waiting state
        }
    }

    console.log("displayed waypoints:", waypoints);

    return (
        <>
            <DragDropContext onDragEnd={handleOnDragEnd}>
                <Droppable droppableId="waypoints">
                    {(provided) => (
                    <ul className="waypoints-container" {...provided.droppableProps} ref={provided.innerRef}>
                        <div className="column p32" style={{ paddingBlock: 16 }}>
                            {/* Ensure all waypoints are rendered */}
                            {waypoints.map(({id, ...waypoint}, index) => (
                                <Draggable key={""+id} draggableId={""+id} index={index}>
                                    {(provided, snapshot) => (
                                        <li className={`waypoint ${snapshot.isDragging ? ' dragging' : ''} ${index == 0 ? 'first' : ''} ${index == (waypoints.length - 1) ? 'last' : ''}`} key={""+id} ref={provided.innerRef} {...provided.draggableProps}>
                                            <div className="column g4 grow">
                                                <div className="line g16 center-cross">
                                                    <p className="type grow">{index != 0 ? (index != (waypoints.length - 1) ? "PEATUS" : "LÕPP" ) : "START"}</p>
                                                    <div className="row g16 center-cross" style={{paddingInline: 0}}>
                                                        <div className='row center-cross' {...provided.dragHandleProps}>
                                                            <svg xmlns="http://www.w3.org/2000/svg" width="48" height="16" viewBox="0 0 48 16" fill="none"><rect y="7" width="48" height="2" fill="#e0e0e0e0"/><rect y="11" width="48" height="2" fill="#e0e0e0e0"/><rect y="3" width="48" height="2" fill="#e0e0e0e0"/></svg>
                                                        </div>
                                                    </div>
                                                </div>
                                                { index != 0 && waypoint.arrive ? (
                                                    <div className="row g8 w100 center-cross">
                                                        <p style={{ color: '#666' }}>Eeldatav saabumine: {unixToEstonianDateTimeString(waypoint.arrive)}</p>
                                                    </div>
                                                ) : <></> }
                                                <div className="row g8 w100">
                                                    <RouteAddress 
                                                        className="address grow" 
                                                        id={id} 
                                                        onNeedsEstimationUpdate={scheduleUpdateEstimation} 
                                                    />
                                                    {/* Only show delete if more than 1 waypoint exists */}
                                                    { waypoints.length > 1 && (
                                                        <div className="row center-main center-cross" onClick={() => handleOnDelete(id)} style={{ backgroundColor: '#f88', borderRadius: 4, padding: 4, width: 32, height: 32, cursor: 'pointer' }}>
                                                            <svg fill="#fff" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 50 50" width="12px" height="12px"><path d="M 7.71875 6.28125 L 6.28125 7.71875 L 23.5625 25 L 6.28125 42.28125 L 7.71875 43.71875 L 25 26.4375 L 42.28125 43.71875 L 43.71875 42.28125 L 26.4375 25 L 43.71875 7.71875 L 42.28125 6.28125 L 25 23.5625 Z" stroke='#fff' strokeWidth={4}></path></svg>
                                                        </div>
                                                    )}
                                                </div>
                                                {/* Show depart input for all except the last waypoint */}
                                                { index !== (waypoints.length - 1) ? (
                                                    <div className="row g8 w100">
                                                        <p className="p8" style={{ fontSize: 12, fontWeight: '600', color: '#888', paddingBlock: 6, minWidth: 70 }}>VÄLJUMINE:</p>
                                                        <div className="column">
                                                            <div className="column g4 grow">
                                                                <input
                                                                    className='grow'
                                                                    style={{ backgroundColor: 'white', borderRadius: 4, width: 'min-content', maxHeight: 28, fontSize: 15, paddingLeft: 3, paddingRight: 0, ...(waypoint.depart ? {} : { borderColor: '#f88' }) }}
                                                                    type='datetime-local'
                                                                    // Modified: Use helper for value conversion
                                                                    value={unixToDateTimeLocalString(waypoint.depart)}
                                                                    max={'9999-12-31T23:59'}
                                                                    // Modified: Pass e.target.value directly
                                                                    onChange={e => handleChangeDepart(id, e.target.value)}
                                                                />
                                                            </div>
                                                            { index != 0 && waypoint.arrive ? (
                                                                <a style={{ color: '#888', fontSize: 14, borderBottom: 'none', textDecoration: 'underline', cursor: 'pointer' }} onClick={() => sameDepartAsArrive(id)}>Muuda samaks, mis saabumine</a>
                                                            ) : (<></>) }
                                                        </div>
                                                    </div>
                                                ) : <></> }
                                            </div>
                                        </li>
                                    )}
                                </Draggable>
                            ))}
                        </div>

                        {provided.placeholder}
                    </ul>
                    )}
                </Droppable>
            </DragDropContext>

            {/* Only show search if max waypoints not reached (e.g., limit 10) */}
            { waypoints.length < 10 && <RouteWaypointSearch className="search" onSubmit={addWaypoint}/> }
        </>
    );
};

export default RouteDisplay;